import { useReveal } from "../lib/useReveal";
import { ArrowRight, BookOpenText, HeartPulse, Compass, Newspaper } from "lucide-react";

const RESOURCES = [
    {
        Icon: BookOpenText,
        eyebrow: "Guides",
        title: "Daily recovery tools you can actually use",
        body: "Grounding prompts, journaling ideas, and check-in rituals that make hard days feel more manageable.",
        href: "#faq",
        cta: "Explore support tools",
        tone: "bg-emerald-100 text-emerald-700",
    },
    {
        Icon: HeartPulse,
        eyebrow: "Support",
        title: "What to do when cravings hit or emotions spike",
        body: "Practical, compassionate guidance for moments that feel urgent, lonely, or overwhelming.",
        href: "#contact",
        cta: "See crisis support",
        tone: "bg-rose-100 text-rose-700",
    },
    {
        Icon: Compass,
        eyebrow: "Find help",
        title: "Move from private reflection to real-world care",
        body: "Use the directory to browse sober living homes, rehabs, outpatient programs, and community resources.",
        href: "/directory",
        cta: "Browse the directory",
        tone: "bg-blue-100 text-blue-700",
    },
];

export default function Resources() {
    const ref = useReveal();

    return (
        <section
            id="resources"
            ref={ref}
            data-testid="resources-section"
            className="py-20 md:py-24 bg-muted/30 border-y scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-14 reveal">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <Newspaper className="w-4 h-4" />
                            Blog & Resources
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                            Helpful recovery content, not content for content&apos;s sake.
                        </h2>
                        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                            Keep the tone warm, practical, and hopeful. This section gives visitors
                            reasons to trust the platform before they ever open the app.
                        </p>
                    </div>
                    <a
                        href="#contact"
                        className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
                    >
                        Get launch updates
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>

                <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 reveal">
                    <div className="grid md:grid-cols-2 gap-6">
                        {RESOURCES.slice(0, 2).map((item, index) => (
                            <a
                                key={item.title}
                                href={item.href}
                                data-testid={`resource-card-${index}`}
                                className="group rounded-3xl border bg-card p-7 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all"
                            >
                                <div
                                    className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${item.tone} mb-5`}
                                >
                                    <item.Icon className="w-6 h-6" />
                                </div>
                                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
                                    {item.eyebrow}
                                </div>
                                <h3 className="text-2xl font-bold text-foreground tracking-tight leading-tight">
                                    {item.title}
                                </h3>
                                <p className="mt-4 text-muted-foreground leading-relaxed">
                                    {item.body}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                                    {item.cta}
                                    <ArrowRight className="w-4 h-4" />
                                </div>
                            </a>
                        ))}
                    </div>

                    <a
                        href={RESOURCES[2].href}
                        data-testid="resource-feature-card"
                        className="group relative overflow-hidden rounded-[2rem] border bg-slate-950 text-white p-8 md:p-10 min-h-[22rem] flex flex-col justify-end hover:-translate-y-1 transition-transform"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?auto=format&fit=crop&w=1200&q=85"
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover opacity-25"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/25" />
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-4">
                                <Compass className="w-4 h-4" />
                                Find support
                            </div>
                            <h3 className="text-3xl font-bold tracking-tight leading-tight max-w-sm">
                                Private, judgment-free search when you&apos;re ready for more support.
                            </h3>
                            <p className="mt-4 text-white/75 max-w-md leading-relaxed">
                                Frame the directory as a natural next step from Hope: private digital
                                support first, verified real-world resources whenever the moment feels right.
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 text-white font-semibold group-hover:gap-3 transition-all">
                                {RESOURCES[2].cta}
                                <ArrowRight className="w-4 h-4" />
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </section>
    );
}
